import { TranslateService } from '@ngx-translate/core';
import { AppNotification } from '../models/notification';

// TRAVEL / URGENT: "hoy" / "mañana" / "en N días" según params.daysUntil (el Backend no envía el texto traducido).
// Sin daysUntil válido devuelve "—", como el resto de parámetros ausentes.
const urgentWhen = (translate: TranslateService, base: string, notification: AppNotification): string => {
  const days = Number(notification.params?.['daysUntil']);
  if (notification.type !== 'TRAVEL' || notification.event !== 'URGENT' || notification.params?.['daysUntil'] === undefined || !Number.isInteger(days) || days < 0) return '—';
  return days === 0 ? translate.instant(`${base}.when.today`) : days === 1 ? translate.instant(`${base}.when.tomorrow`) : translate.instant(`${base}.when.days`, { days });
};

// Claves ngx-translate: notifications.<type>.<event>.title|message. Para un type/event que aún no
// tiene traducción (los tipos crecerán en el Backend) se usa notifications.generic.*, sin lanzar error.
export const getNotificationText = (translate: TranslateService, notification: AppNotification): { title: string; message: string } => {
  const base = `notifications.${notification.type}.${notification.event}`;
  const exists = ['title', 'message'].every((part) => translate.instant(`${base}.${part}`) !== `${base}.${part}`);
  const key = exists ? base : 'notifications.generic';
  // Los parámetros que falten se muestran como "—" en lugar de dejar la marca {{...}} en el texto
  const params = {
    ...notification.params,
    when: urgentWhen(translate, base, notification),
    proofIncomeNumber: notification.params?.proofIncomeNumber ?? '—',
    passengerName: notification.params?.['passengerName'] ?? '—',
    travelReference: notification.params?.['travelReference'] ?? '—',
    customerName: notification.params?.['customerName'] ?? '—',
    tripName: notification.params?.['tripName'] ?? '—',
    available: notification.params?.['available'] ?? '—',
    count: notification.params?.['count'] ?? '—',
  };
  return {
    title: translate.instant(`${key}.title`, params),
    message: translate.instant(`${key}.message`, params),
  };
};

// ---------------------------------------------------------------------------
// Destino de una notificación. La resolución usa solo datos estructurados (type, event, entityType,
// entityId, params), nunca texto traducido, y solo reconoce destinos explícitamente soportados: para
// cualquier otro type/event/entityType devuelve UNSUPPORTED (no se adivinan rutas).
// ---------------------------------------------------------------------------
export type NotificationTarget =
  | { kind: 'INCOME_RECEIPT_DEPOSIT'; depositId: string; travelId?: string; status?: number; proofIncomeNumber?: string }
  | { kind: 'TRAVEL'; travelId: string }
  | { kind: 'TRAVEL_DIGEST'; travelTypes: number[] }
  | { kind: 'ALEXA_RESERVATION'; reservationId: string }
  | { kind: 'UNSUPPORTED' };

// Eventos que el Backend genera hoy para INCOME_RECEIPT
const INCOME_RECEIPT_EVENTS = ['CREATED', 'STATUS_PENDING', 'STATUS_CONFIRMED', 'STATUS_CANCELLED', 'STATUS_REFUNDED', 'STATUS_REJECTED'];
// Eventos TRAVEL cuyo destino es el detalle del viaje
const TRAVEL_DETAIL_EVENTS = ['PASSENGER_ADDED', 'URGENT'];
const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

export const resolveNotificationTarget = (notification: AppNotification): NotificationTarget => {
  if (
    notification?.type === 'INCOME_RECEIPT' &&
    notification.entityType === 'DEPOSIT' &&
    INCOME_RECEIPT_EVENTS.includes(notification.event) &&
    typeof notification.entityId === 'string' &&
    OBJECT_ID_REGEX.test(notification.entityId)
  ) {
    const status = Number(notification.params?.status);
    const proofIncomeNumber = notification.params?.proofIncomeNumber;
    const travelId = notification.params?.travelId;
    return {
      kind: 'INCOME_RECEIPT_DEPOSIT',
      depositId: notification.entityId,
      travelId: typeof travelId === 'string' && OBJECT_ID_REGEX.test(travelId) ? travelId : undefined,
      status: Number.isInteger(status) ? status : undefined,
      proofIncomeNumber: typeof proofIncomeNumber === 'string' && proofIncomeNumber ? proofIncomeNumber : undefined,
    };
  }
  // TRAVEL / PASSENGER_ADDED: el destino es el detalle del viaje (params.travelId; el Backend también guarda
  // el viaje como entityId). Nunca se usa el id del pasajero como id del viaje.
  // TRAVEL / URGENT (viaje urgente): mismo destino que PASSENGER_ADDED, el detalle del viaje.
  if (notification?.type === 'TRAVEL' && TRAVEL_DETAIL_EVENTS.includes(notification.event) && notification.entityType === 'TRAVEL') {
    const travelId = notification.params?.travelId ?? notification.entityId;
    return typeof travelId === 'string' && OBJECT_ID_REGEX.test(travelId) ? { kind: 'TRAVEL', travelId } : { kind: 'UNSUPPORTED' };
  }
  // TRAVEL / URGENT_SUMMARY: resumen de varios viajes (entityType TRAVEL_DIGEST; su entityId es un ObjectId derivado de
  // la fecha, NO un viaje). El destino es la lista de viajes; params.travelTypes (tipos de viaje que cubre el resumen)
  // sólo sirve para elegir la lista.
  if (notification?.type === 'TRAVEL' && notification.event === 'URGENT_SUMMARY' && notification.entityType === 'TRAVEL_DIGEST') {
    if (typeof notification.entityId !== 'string' || !OBJECT_ID_REGEX.test(notification.entityId)) return { kind: 'UNSUPPORTED' };
    const types = notification.params?.['travelTypes'];
    return { kind: 'TRAVEL_DIGEST', travelTypes: Array.isArray(types) ? types.filter((type: unknown): type is number => type === 1 || type === 2 || type === 3) : [] };
  }
  // ALEXA_RESERVATION / CREATED: el destino es la pantalla de reservas de Alexa con esa reserva resaltada. El id sale
  // de entityId (el Backend guarda la reserva como entidad), nunca de una URL del payload.
  if (notification?.type === 'ALEXA_RESERVATION' && notification.event === 'CREATED' && notification.entityType === 'ALEXA_RESERVATION') {
    return typeof notification.entityId === 'string' && OBJECT_ID_REGEX.test(notification.entityId)
      ? { kind: 'ALEXA_RESERVATION', reservationId: notification.entityId }
      : { kind: 'UNSUPPORTED' };
  }
  return { kind: 'UNSUPPORTED' };
};

// Un depósito solo se abre en DetailProofComponent si es el solicitado (mismo _id) y llegó con la forma
// completa que devuelve el Backend (relaciones pobladas, no ids sueltos). Nunca se construye un
// depósito parcial a partir de la notificación.
export const isCompleteDeposit = (deposit: any, depositId: string): boolean =>
  !!deposit &&
  typeof deposit === 'object' &&
  String(deposit._id) === depositId &&
  typeof deposit.status === 'number' &&
  Array.isArray(deposit.concepts) &&
  typeof deposit.idTravel !== 'string' &&
  typeof deposit.clientId !== 'string';
